import React, { useEffect, useRef, useState } from "react";
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase";
import { FiX, FiDownload, FiFilm, FiAlertTriangle } from "react-icons/fi";
import logoUrl from "../assets/logo.png";

// ─── Design constants (matches VoiceoverEditor/AddLesson) ─────────────
const TEAL = "#2a9d8f";
const RED = "#e76f51";
const OUT_W = 1920;
const OUT_H = 1080;

const MIME_CANDIDATES = ["video/webm;codecs=vp9,opus", "video/webm;codecs=vp8,opus", "video/webm"];
function pickMimeType() {
    if (typeof MediaRecorder === "undefined") return "";
    return MIME_CANDIDATES.find((t) => MediaRecorder.isTypeSupported(t)) || "";
}

function isExportSupported() {
    return typeof window !== "undefined"
        && typeof window.MediaRecorder === "function"
        && typeof window.HTMLCanvasElement !== "undefined"
        && typeof HTMLCanvasElement.prototype.captureStream === "function";
}

// Same "direct fetch, fall back to a server-side Cloud Function" dance as
// VoiceoverEditor's fetchAudioArrayBuffer — the Storage bucket has no CORS
// policy, so a plain fetch() of a basics_audio/images download URL is
// blocked cross-origin from the CMS's own origin. Loading images this way
// (rather than a plain <img src={url}>) also matters here specifically
// because a cross-origin image without CORS headers taints the canvas,
// which silently produces a blank/black recording from captureStream().
function extractStoragePath(url) {
    const match = url.match(/\/o\/([^?]+)/);
    return match ? decodeURIComponent(match[1]) : null;
}

function base64ToBlob(base64, contentType) {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return new Blob([bytes], { type: contentType });
}

async function fetchAssetBlob(url, callableName) {
    try {
        const res = await fetch(url);
        if (!res.ok) throw new Error("bad response");
        return await res.blob();
    } catch {
        const path = extractStoragePath(url);
        if (!path) throw new Error("Could not determine the storage path for one of the assets.");
        const fn = httpsCallable(functions, callableName);
        const result = await fn({ path });
        const base64 = result?.data?.base64;
        if (!base64) throw new Error("Server returned no data for one of the assets.");
        return base64ToBlob(base64, result?.data?.contentType || "application/octet-stream");
    }
}

function loadImage(src) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error("Failed to decode image"));
        img.src = src;
    });
}

// Cached so the export's per-frame redraw doesn't re-decode the logo.
let logoImagePromise = null;
function getLogoImage() {
    if (!logoImagePromise) logoImagePromise = loadImage(logoUrl).catch(() => null);
    return logoImagePromise;
}

// ─── Canvas drawing ─────────────────────────────────────────────────
function roundRectPath(ctx, x, y, w, h, r) {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
}

function drawImageCover(ctx, img, x, y, w, h, radius) {
    ctx.save();
    roundRectPath(ctx, x, y, w, h, radius);
    ctx.clip();
    const scale = Math.max(w / img.width, h / img.height);
    const sw = w / scale, sh = h / scale;
    const sx = (img.width - sw) / 2, sy = (img.height - sh) / 2;
    ctx.drawImage(img, sx, sy, sw, sh, x, y, w, h);
    ctx.restore();
}

// Canvas has no built-in word-wrap — measure and break manually. ctx.font
// must already be set to the font these lines will be measured/drawn with.
function computeWrapLines(ctx, text, maxWidth, maxLines) {
    const words = String(text).split(/\s+/).filter(Boolean);
    const lines = [];
    let line = "";
    for (const word of words) {
        const test = line ? `${line} ${word}` : word;
        if (line && ctx.measureText(test).width > maxWidth) {
            lines.push(line);
            line = word;
        } else {
            line = test;
        }
    }
    if (line) lines.push(line);

    let out = lines;
    if (maxLines && out.length > maxLines) {
        out = out.slice(0, maxLines);
        out[maxLines - 1] = out[maxLines - 1].replace(/\s*\S*$/, "…");
    }
    return out;
}

// Draws left-aligned wrapped text and returns the y position just below the
// last line drawn.
function wrapText(ctx, text, x, y, maxWidth, lineHeight, maxLines) {
    const lines = computeWrapLines(ctx, text, maxWidth, maxLines);
    lines.forEach((l, i) => ctx.fillText(l, x, y + i * lineHeight));
    return y + lines.length * lineHeight;
}

function drawWordmark(ctx, x, y, color, logoImg) {
    const fontSize = 34;
    let textX = x;
    if (logoImg) {
        const iconSize = fontSize;
        ctx.drawImage(logoImg, x, y - iconSize + 6, iconSize, iconSize);
        textX = x + iconSize + 10;
    }
    ctx.fillStyle = color;
    ctx.font = `700 ${fontSize}px Quicksand, sans-serif`;
    ctx.textAlign = "left";
    ctx.textBaseline = "alphabetic";
    ctx.fillText("Kubaya", textX, y);
}

// Draws the logo + "Kubaya" centered as one group around cx, matching the
// textBaseline: "middle" the caller has already set.
function drawWordmarkCentered(ctx, cx, y, fontSize, color, logoImg) {
    ctx.font = `700 ${fontSize}px Quicksand, sans-serif`;
    ctx.textAlign = "left";
    const textWidth = ctx.measureText("Kubaya").width;
    const iconSize = fontSize * 0.85;
    const gap = fontSize * 0.28;
    const totalWidth = (logoImg ? iconSize + gap : 0) + textWidth;
    let x = cx - totalWidth / 2;
    if (logoImg) {
        ctx.drawImage(logoImg, x, y - iconSize / 2, iconSize, iconSize);
        x += iconSize + gap;
    }
    ctx.fillStyle = color;
    ctx.fillText("Kubaya", x, y);
}

function drawIntro(ctx, W, H, title, concept, logoImg) {
    ctx.fillStyle = TEAL;
    ctx.fillRect(0, 0, W, H);

    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    drawWordmarkCentered(ctx, W / 2, H / 2 - 120, 96, "#ffffff", logoImg);

    ctx.font = "700 64px Quicksand, sans-serif";
    ctx.textBaseline = "top";
    ctx.textAlign = "left";
    wrapText(ctx, title || "Amharic Basics", W / 2 - 700, H / 2 - 10, 1400, 76, 2);

    if (concept) {
        ctx.font = "600 34px Quicksand, sans-serif";
        ctx.fillStyle = "#eafaf6";
        ctx.textAlign = "center";
        ctx.fillText(concept, W / 2, H / 2 + 150);
    }
}

function drawOutro(ctx, W, H, logoImg) {
    ctx.fillStyle = TEAL;
    ctx.fillRect(0, 0, W, H);

    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    drawWordmarkCentered(ctx, W / 2, H / 2 - 40, 90, "#ffffff", logoImg);

    ctx.font = "600 36px Quicksand, sans-serif";
    ctx.fillStyle = "#eafaf6";
    ctx.textAlign = "center";
    ctx.fillText("Learn Amharic, one cup at a time.", W / 2, H / 2 + 50);
}

// Caption bar: a translucent rounded box near the bottom of the frame,
// holding the emphasis word/phrase (if any) above the body script text —
// styled like standard video subtitles rather than a side panel, so it reads
// the same whether or not the segment has an image.
function drawCaptionBar(ctx, W, H, seg) {
    const emphasis = (seg.emphasisText || "").trim();
    const body = (seg.text || "").trim();
    if (!emphasis && !body) return;

    const PAD_X = 140;
    const maxWidth = W - PAD_X * 2;
    const emphasisFont = "700 52px Quicksand, sans-serif";
    const bodyFont = "600 36px Quicksand, sans-serif";
    const emphasisLH = 62;
    const bodyLH = 48;
    const innerPadY = 34;
    const gapBetween = emphasis && body ? 16 : 0;
    const bottomMargin = 64; // stays clear of the progress bar

    ctx.font = emphasisFont;
    const emphasisLines = emphasis ? computeWrapLines(ctx, emphasis, maxWidth, 2) : [];
    ctx.font = bodyFont;
    const bodyLines = body ? computeWrapLines(ctx, body, maxWidth, 3) : [];

    const contentH = emphasisLines.length * emphasisLH + bodyLines.length * bodyLH + gapBetween;
    const boxH = contentH + innerPadY * 2;
    const boxW = W - (PAD_X - 40) * 2;
    const boxX = (W - boxW) / 2;
    const boxY = H - bottomMargin - boxH;

    ctx.fillStyle = "rgba(0,0,0,0.6)";
    roundRectPath(ctx, boxX, boxY, boxW, boxH, 22);
    ctx.fill();

    ctx.textAlign = "center";
    ctx.textBaseline = "alphabetic";
    let cursorY = boxY + innerPadY + emphasisLH * 0.72;

    if (emphasisLines.length) {
        ctx.font = emphasisFont;
        ctx.fillStyle = "#e9c46a";
        emphasisLines.forEach((l, i) => ctx.fillText(l, W / 2, cursorY + i * emphasisLH));
        cursorY += emphasisLines.length * emphasisLH + gapBetween;
    } else {
        cursorY = boxY + innerPadY + bodyLH * 0.72;
    }

    if (bodyLines.length) {
        ctx.font = bodyFont;
        ctx.fillStyle = "#ffffff";
        bodyLines.forEach((l, i) => ctx.fillText(l, W / 2, cursorY + i * bodyLH));
    }
}

function drawSegment(ctx, W, H, seg, logoImg) {
    ctx.fillStyle = TEAL;
    ctx.fillRect(0, 0, W, H);

    if (seg.img) {
        drawImageCover(ctx, seg.img, 0, 0, W, H, 0);
        // Scrims top and bottom so the wordmark and captions stay legible
        // over any image, regardless of its own brightness/contrast.
        const topGrad = ctx.createLinearGradient(0, 0, 0, 220);
        topGrad.addColorStop(0, "rgba(0,0,0,0.45)");
        topGrad.addColorStop(1, "rgba(0,0,0,0)");
        ctx.fillStyle = topGrad;
        ctx.fillRect(0, 0, W, 220);

        const bottomGrad = ctx.createLinearGradient(0, H * 0.55, 0, H);
        bottomGrad.addColorStop(0, "rgba(0,0,0,0)");
        bottomGrad.addColorStop(1, "rgba(0,0,0,0.35)");
        ctx.fillStyle = bottomGrad;
        ctx.fillRect(0, H * 0.55, W, H * 0.45);
    }

    drawWordmark(ctx, 56, 88, "#ffffff", logoImg);
    drawCaptionBar(ctx, W, H, seg);
}

function drawProgressBar(ctx, W, H, currentTime, totalDuration) {
    const barH = 8;
    const frac = totalDuration > 0 ? Math.min(1, Math.max(0, currentTime / totalDuration)) : 0;
    ctx.fillStyle = "rgba(0,0,0,0.15)";
    ctx.fillRect(0, H - barH, W, barH);
    ctx.fillStyle = "#e9c46a";
    ctx.fillRect(0, H - barH, W * frac, barH);
}

function draw(ctx, W, H, { segments, totalDuration, currentTime, lessonTitle, concept, logoImg }) {
    const first = segments[0];
    const last = segments[segments.length - 1];

    if (!first || currentTime < first.start) {
        drawIntro(ctx, W, H, lessonTitle, concept, logoImg);
    } else if (currentTime >= last.end) {
        drawOutro(ctx, W, H, logoImg);
    } else {
        let active = first;
        for (const seg of segments) {
            if (currentTime >= seg.start) active = seg; else break;
        }
        drawSegment(ctx, W, H, active, logoImg);
    }

    drawProgressBar(ctx, W, H, currentTime, totalDuration);
}

function formatTime(t) {
    if (!isFinite(t) || t < 0) return "0:00";
    const m = Math.floor(t / 60);
    const s = Math.floor(t % 60).toString().padStart(2, "0");
    return `${m}:${s}`;
}

// ─── Component ──────────────────────────────────────────────────────
// Renders a Basics lesson's voiceover segments (image + emphasis text +
// body text, per VoiceoverEditor/PhonePreview) onto a 1920x1080 canvas in
// sync with the recorded narration, capturing the result live via
// MediaRecorder into a downloadable .webm — suitable for uploading straight
// to YouTube (which accepts .webm uploads natively) without any server-side
// rendering pipeline.
function VideoExportModal({ lesson, onClose }) {
    const [phase, setPhase] = useState("idle"); // idle | loading-assets | recording | done | error
    const [error, setError] = useState("");
    const [progress, setProgress] = useState({ currentTime: 0, duration: 0 });
    const [result, setResult] = useState(null);

    const audioElRef = useRef(null);
    const canvasRef = useRef(null);
    const recorderRef = useRef(null);
    const chunksRef = useRef([]);
    // Drives the per-frame redraw via setInterval rather than
    // requestAnimationFrame: rAF gets throttled to near-zero (sometimes fully
    // suspended) in backgrounded/minimized tabs, which let the canvas freeze
    // on one segment's caption for the rest of the export while the audio
    // (unaffected by that throttling) kept right on playing. setInterval is
    // only rate-limited (not stopped) when hidden, so captions keep advancing.
    const tickTimerRef = useRef(null);
    const objectUrlsRef = useRef([]);

    const open = !!lesson;

    useEffect(() => {
        if (!open) return;
        setPhase("idle");
        setError("");
        setResult(null);
        setProgress({ currentTime: 0, duration: 0 });
        const audioEl = audioElRef.current;
        return () => {
            clearInterval(tickTimerRef.current);
            const rec = recorderRef.current;
            if (rec && rec.state !== "inactive") rec.stop();
            if (audioEl) audioEl.pause();
            objectUrlsRef.current.forEach((u) => URL.revokeObjectURL(u));
            objectUrlsRef.current = [];
        };
        // eslint-disable-next-line
    }, [lesson?.id]);

    if (!open) return null;

    const segmentsRaw = Array.isArray(lesson.voiceoverSegments) ? lesson.voiceoverSegments : [];
    const hasVoiceover = !!lesson.voiceoverAudioUrl && segmentsRaw.length > 0;
    const supported = isExportSupported();

    const runExport = async () => {
        setError("");
        setResult(null);
        setPhase("loading-assets");
        try {
            const segsSorted = [...segmentsRaw].sort((a, b) => a.start - b.start);

            const logoImg = await getLogoImage();
            const audioBlob = await fetchAssetBlob(lesson.voiceoverAudioUrl, "fetchBasicsAudio");
            const audioUrl = URL.createObjectURL(audioBlob);
            objectUrlsRef.current.push(audioUrl);

            const segments = await Promise.all(segsSorted.map(async (seg) => {
                if (!seg.imageUrl) return { ...seg, img: null };
                try {
                    const blob = await fetchAssetBlob(seg.imageUrl, "fetchBasicsImage");
                    const url = URL.createObjectURL(blob);
                    objectUrlsRef.current.push(url);
                    const img = await loadImage(url);
                    return { ...seg, img };
                } catch {
                    return { ...seg, img: null };
                }
            }));

            const audioEl = audioElRef.current;
            audioEl.src = audioUrl;
            await new Promise((resolve, reject) => {
                audioEl.onloadedmetadata = resolve;
                audioEl.onerror = () => reject(new Error("Could not load the recorded audio."));
            });

            if (typeof audioEl.captureStream !== "function") {
                throw new Error("Your browser doesn't support in-browser video capture. Try Chrome or Edge on desktop.");
            }

            const totalDuration = Number.isFinite(lesson.voiceoverDuration) && lesson.voiceoverDuration > 0
                ? lesson.voiceoverDuration
                : audioEl.duration;

            const canvas = canvasRef.current;
            canvas.width = OUT_W;
            canvas.height = OUT_H;
            const ctx = canvas.getContext("2d");
            const drawArgs = { segments, totalDuration, lessonTitle: lesson.title || "", concept: lesson.concept || "", logoImg };
            draw(ctx, OUT_W, OUT_H, { ...drawArgs, currentTime: 0 });

            const videoStream = canvas.captureStream(30);
            const audioStream = audioEl.captureStream();
            const combined = new MediaStream([...videoStream.getVideoTracks(), ...audioStream.getAudioTracks()]);

            const mimeType = pickMimeType();
            const recorder = new MediaRecorder(combined, mimeType ? { mimeType, videoBitsPerSecond: 6_000_000 } : undefined);
            chunksRef.current = [];
            recorder.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
            recorderRef.current = recorder;
            const stopped = new Promise((resolve) => { recorder.onstop = resolve; });

            setPhase("recording");

            const tick = () => {
                draw(ctx, OUT_W, OUT_H, { ...drawArgs, currentTime: audioEl.currentTime });
                setProgress({ currentTime: audioEl.currentTime, duration: totalDuration });
            };

            recorder.start();
            await audioEl.play();
            tickTimerRef.current = setInterval(tick, 1000 / 30);

            await new Promise((resolve) => { audioEl.onended = resolve; });
            clearInterval(tickTimerRef.current);
            draw(ctx, OUT_W, OUT_H, { ...drawArgs, currentTime: totalDuration });
            recorder.stop();
            await stopped;

            const outBlob = new Blob(chunksRef.current, { type: mimeType || "video/webm" });
            const outUrl = URL.createObjectURL(outBlob);
            objectUrlsRef.current.push(outUrl);
            const safeTitle = (lesson.title || "kubaya-lesson").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
            setResult({ url: outUrl, filename: `${safeTitle || "kubaya-lesson"}-basics.webm`, size: outBlob.size });
            setPhase("done");
        } catch (err) {
            clearInterval(tickTimerRef.current);
            setError(err?.message || "Export failed.");
            setPhase("error");
        }
    };

    const cancelExport = () => {
        clearInterval(tickTimerRef.current);
        const rec = recorderRef.current;
        if (rec && rec.state !== "inactive") rec.stop();
        const audioEl = audioElRef.current;
        if (audioEl) audioEl.pause();
        setPhase("idle");
    };

    const busy = phase === "loading-assets" || phase === "recording";

    return (
        <div
            onClick={busy ? undefined : onClose}
            style={{
                position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
                display: "flex", alignItems: "center", justifyContent: "center",
                zIndex: 1000, padding: 24,
            }}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                style={{
                    background: "#fff", borderRadius: 18, width: "100%", maxWidth: 720,
                    maxHeight: "90vh", display: "flex", flexDirection: "column",
                    boxShadow: "0 12px 40px rgba(0,0,0,0.28)", overflow: "hidden",
                    fontFamily: "'Quicksand', sans-serif",
                }}
            >
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px", borderBottom: "1px solid #eee", gap: 12 }}>
                    <div style={{ minWidth: 0, display: "flex", alignItems: "center", gap: 10 }}>
                        <FiFilm size={18} color={TEAL} />
                        <div>
                            <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "#888" }}>
                                Export as Video
                            </div>
                            <div style={{ fontSize: 14, fontWeight: 700, color: "#000", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 480 }}>
                                {lesson.title || "Untitled lesson"}
                            </div>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={busy ? cancelExport : onClose}
                        title={busy ? "Cancel export" : "Close"}
                        style={{ width: 36, height: 36, borderRadius: 10, border: "1.5px solid #E0E0E0", background: "#fff", color: "#606060", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
                    >
                        <FiX size={16} />
                    </button>
                </div>

                <div style={{ padding: 20, overflow: "auto" }}>
                    {!supported && (
                        <div style={{ display: "flex", gap: 10, alignItems: "flex-start", background: "#fff8e6", border: "1px solid #e9c46a", borderRadius: 12, padding: "12px 14px", marginBottom: 16, fontSize: 13, fontWeight: 600, color: "#8a6d1a" }}>
                            <FiAlertTriangle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
                            <span>Your browser doesn't support in-browser video capture (canvas/audio <code>captureStream</code>). Try Chrome or Edge on desktop.</span>
                        </div>
                    )}

                    {!hasVoiceover && (
                        <div style={{ fontSize: 13.5, fontWeight: 600, color: "#606060", background: "#f7f9fb", border: "1px solid #E0E0E0", borderRadius: 12, padding: "14px 16px", marginBottom: 16 }}>
                            This lesson has no recorded voiceover yet. Add narration and segment images in the Basics editor first, then come back here to export a video.
                        </div>
                    )}

                    {hasVoiceover && (
                        <div style={{ fontSize: 13, fontWeight: 600, color: "#606060", marginBottom: 16 }}>
                            {segmentsRaw.length} segment{segmentsRaw.length !== 1 ? "s" : ""}
                            {Number.isFinite(lesson.voiceoverDuration) ? ` · ${formatTime(lesson.voiceoverDuration)} narration` : ""}
                            {" · exports as 1920×1080 .webm (YouTube accepts .webm uploads directly)"}
                        </div>
                    )}

                    <canvas
                        ref={canvasRef}
                        width={OUT_W}
                        height={OUT_H}
                        style={{
                            width: "100%", aspectRatio: "16 / 9", borderRadius: 12,
                            border: "1.5px solid #E0E0E0", background: "#f0f0f0",
                            display: phase === "idle" && !result ? "none" : "block",
                            marginBottom: 16,
                        }}
                    />
                    {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                    <audio ref={audioElRef} style={{ display: "none" }} />

                    {phase === "recording" && (
                        <div style={{ marginBottom: 16 }}>
                            <div style={{ height: 8, borderRadius: 999, background: "#eee", overflow: "hidden" }}>
                                <div style={{
                                    height: "100%", background: TEAL, borderRadius: 999,
                                    width: `${progress.duration > 0 ? Math.min(100, (progress.currentTime / progress.duration) * 100) : 0}%`,
                                    transition: "width 100ms linear",
                                }} />
                            </div>
                            <div style={{ fontSize: 12, fontWeight: 700, color: "#606060", marginTop: 6 }}>
                                Recording… {formatTime(progress.currentTime)} / {formatTime(progress.duration)} — keep this tab open until it finishes (it's fine to switch to another tab).
                            </div>
                        </div>
                    )}

                    {phase === "loading-assets" && (
                        <div style={{ fontSize: 13, fontWeight: 700, color: TEAL, marginBottom: 16 }}>
                            Loading audio and images…
                        </div>
                    )}

                    {phase === "error" && error && (
                        <div style={{ display: "flex", gap: 10, alignItems: "flex-start", background: "#fdeeea", border: "1px solid #e76f51", borderRadius: 12, padding: "12px 14px", marginBottom: 16, fontSize: 13, fontWeight: 600, color: "#a8402a" }}>
                            <FiAlertTriangle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
                            <span>{error}</span>
                        </div>
                    )}

                    {phase === "done" && result && (
                        <div style={{ background: "#eafaf3", border: "1px solid #50C878", borderRadius: 12, padding: "12px 14px", marginBottom: 16, fontSize: 13, fontWeight: 700, color: "#2f8a56" }}>
                            Video ready ({(result.size / (1024 * 1024)).toFixed(1)} MB). Download it below, then upload the .webm file straight to YouTube Studio.
                        </div>
                    )}

                    <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", flexWrap: "wrap" }}>
                        {phase === "recording" || phase === "loading-assets" ? (
                            <button
                                type="button"
                                onClick={cancelExport}
                                style={{
                                    display: "inline-flex", alignItems: "center", gap: 7, padding: "10px 18px", borderRadius: 10,
                                    border: `1.5px solid ${RED}`, background: "#fff", color: RED,
                                    fontWeight: 700, fontSize: 14, cursor: "pointer", fontFamily: "'Quicksand', sans-serif",
                                }}
                            >
                                Cancel
                            </button>
                        ) : phase === "done" && result ? (
                            <>
                                <button
                                    type="button"
                                    onClick={runExport}
                                    style={{
                                        display: "inline-flex", alignItems: "center", gap: 7, padding: "10px 18px", borderRadius: 10,
                                        border: "1.5px solid #E0E0E0", background: "#fff", color: "#606060",
                                        fontWeight: 700, fontSize: 14, cursor: "pointer", fontFamily: "'Quicksand', sans-serif",
                                    }}
                                >
                                    Export again
                                </button>
                                <a
                                    href={result.url}
                                    download={result.filename}
                                    style={{
                                        display: "inline-flex", alignItems: "center", gap: 7, padding: "10px 18px", borderRadius: 10,
                                        border: "1.5px solid #258D80", borderBottomWidth: 3, background: TEAL, color: "#fff",
                                        fontWeight: 700, fontSize: 14, cursor: "pointer", fontFamily: "'Quicksand', sans-serif",
                                        textDecoration: "none", maxWidth: "100%",
                                    }}
                                >
                                    <FiDownload size={15} style={{ flexShrink: 0 }} />
                                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>Download {result.filename}</span>
                                </a>
                            </>
                        ) : (
                            <button
                                type="button"
                                onClick={runExport}
                                disabled={!hasVoiceover || !supported}
                                style={{
                                    display: "inline-flex", alignItems: "center", gap: 7, padding: "10px 18px", borderRadius: 10,
                                    border: "1.5px solid #258D80", borderBottomWidth: 3,
                                    background: (!hasVoiceover || !supported) ? "#ccc" : TEAL,
                                    color: "#fff", fontWeight: 700, fontSize: 14,
                                    cursor: (!hasVoiceover || !supported) ? "not-allowed" : "pointer",
                                    fontFamily: "'Quicksand', sans-serif",
                                }}
                            >
                                <FiFilm size={15} /> Start export
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default VideoExportModal;
